import React, { useState, useEffect } from 'react';
import { Timeline, Card, Button, Modal, Form, Input, message, Space, Tag, Spin, DatePicker } from 'antd';
import { RollbackOutlined, ReloadOutlined } from '@ant-design/icons';
import { StateHistoryService } from '../../services/api/stateHistoryService';
import { StateMachineService } from '../../services/api/stateMachineService';
import { ArtifactItemStateHistory } from '../../types/artifactItems';
import { TransitionRollbackRequest } from '../../types/stateMachine';
import dayjs from 'dayjs';

const { TextArea } = Input;

interface StateHistoryViewerProps {
  itemId: string;
  artifactId?: string;
  onRollbackComplete?: () => void;
  compact?: boolean;
}

export const StateHistoryViewer: React.FC<StateHistoryViewerProps> = ({
  itemId,
  artifactId,
  onRollbackComplete,
  compact = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<ArtifactItemStateHistory[]>([]);
  const [rollbackModalVisible, setRollbackModalVisible] = useState(false);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState<ArtifactItemStateHistory | null>(null);
  const [rollbackLoading, setRollbackLoading] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (itemId) {
      void loadHistory();
    }
  }, [itemId]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      // Pass artifactId as companyId to use the correct endpoint
      const historyData = await StateHistoryService.getItemStateHistory(itemId, artifactId);
      setHistory(historyData);
    } catch (error: any) {
      console.error('Error loading state history:', error);
      message.error('Failed to load state history');
    } finally {
      setLoading(false);
    }
  };

  const handleRollback = (historyItem: ArtifactItemStateHistory) => {
    setSelectedHistoryItem(historyItem);
    setRollbackModalVisible(true);
    form.resetFields();
  };

  const executeRollback = async () => {
    if (!selectedHistoryItem || !artifactId) return;

    try {
      const values = await form.validateFields();
      setRollbackLoading(true);

      const rollbackData: TransitionRollbackRequest = {
        transition_history_id: selectedHistoryItem.id,
        reason: values.reason || '',
        rolled_back_by: values.rolled_back_by,
      };

      await StateMachineService.rollbackTransition(artifactId, itemId, rollbackData);

      message.success('Transition rolled back successfully');
      setRollbackModalVisible(false);
      setSelectedHistoryItem(null);
      form.resetFields();
      void loadHistory();
      onRollbackComplete?.();
    } catch (error: any) {
      console.error('Error rolling back transition:', error);
      message.error(error.response?.data?.detail || error.message || 'Failed to rollback transition');
    } finally {
      setRollbackLoading(false);
    }
  };

  const canRollback = (historyItem: ArtifactItemStateHistory, index: number) => {
    // Can rollback if it's the most recent transition (index 0)
    return index === 0 && historyItem.transition_id;
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 24 }}>
        <Spin />
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <Card size="small">
        <div style={{ textAlign: 'center', padding: 16, color: '#999' }}>
          No state history available
        </div>
      </Card>
    );
  }

  return (
    <>
      <Card
        title={
          <Space>
            <span>State History</span>
            <Button icon={<ReloadOutlined />} size="small" onClick={loadHistory} loading={loading} />
          </Space>
        }
        size={compact ? 'small' : 'default'}
      >
        <Timeline mode="left" style={{ paddingTop: 16 }}>
          {history.map((item, index) => (
            <Timeline.Item
              key={item.id}
              label={
                <div style={{ minWidth: 150 }}>
                  <div>{dayjs(item.transitioned_at || item.created_at).format('YYYY-MM-DD HH:mm:ss')}</div>
                  {item.transitioned_by && (
                    <div style={{ fontSize: 12, color: '#999' }}>By: {item.transitioned_by}</div>
                  )}
                </div>
              }
            >
              <div>
                <div style={{ marginBottom: 8 }}>
                  <Tag color={index === 0 ? 'blue' : 'default'}>
                    {item.from_state_name || 'Initial'} → {item.to_state_name || 'Unknown'}
                  </Tag>
                  {item.transition_name && (
                    <Tag style={{ marginLeft: 8 }}>{item.transition_name}</Tag>
                  )}
                  {index === 0 && <Tag color="green">Current</Tag>}
                </div>

                {item.transition_reason && (
                  <div style={{ marginBottom: 8, color: '#666', fontSize: 12 }}>
                    <strong>Reason:</strong> {item.transition_reason}
                  </div>
                )}

                {item.transition_data && Object.keys(item.transition_data).length > 0 && (
                  <div style={{ marginBottom: 8, fontSize: 12 }}>
                    <strong>Data:</strong>
                    <pre style={{ marginTop: 4, padding: 8, background: '#f5f5f5', borderRadius: 4 }}>
                      {JSON.stringify(item.transition_data, null, 2)}
                    </pre>
                  </div>
                )}

                {canRollback(item, index) && artifactId && (
                  <Button
                    type="link"
                    size="small"
                    icon={<RollbackOutlined />}
                    onClick={() => handleRollback(item)}
                    style={{ padding: 0 }}
                  >
                    Rollback
                  </Button>
                )}
              </div>
            </Timeline.Item>
          ))}
        </Timeline>
      </Card>

      <Modal
        title="Rollback Transition"
        open={rollbackModalVisible}
        onOk={executeRollback}
        onCancel={() => {
          setRollbackModalVisible(false);
          setSelectedHistoryItem(null);
          form.resetFields();
        }}
        confirmLoading={rollbackLoading}
      >
        {selectedHistoryItem && (
          <div style={{ marginBottom: 16 }}>
            <p>
              <strong>Rolling back:</strong> {selectedHistoryItem.from_state_name || 'Unknown'} →{' '}
              {selectedHistoryItem.to_state_name || 'Unknown'}
            </p>
            {selectedHistoryItem.transition_name && (
              <p>
                <strong>Transition:</strong> {selectedHistoryItem.transition_name}
              </p>
            )}
          </div>
        )}

        <Form form={form} layout="vertical">
          <Form.Item
            label="Reason"
            name="reason"
            rules={[{ required: true, message: 'Please provide a reason for rolling back' }]}
          >
            <TextArea rows={3} placeholder="Why are you rolling back this transition?" />
          </Form.Item>

          <Form.Item label="Rolled Back By (User ID)" name="rolled_back_by">
            <Input placeholder="Optional: User ID who is rolling back" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

